import type { TodayGap } from "@heraion/shared";

import type { CoverageRow, MuscleCoverage } from "./muscleCoverage";
import type { RowWeekFill } from "./muscleCoverageProposal";

// The seam between the Progress coverage rows and the «today» draft
// (todaySessionProposal.ts in shared). Split out: it would push
// muscleCoverage.ts over 350.

export type TodayGapsOpts = {
  /** What lets a muscle into the draft. "week" (the draft's default, Antenore
   *  01/10): under the band THIS week, whatever its 4-week average. "average":
   *  the Progress list's rule, under it on both. */
  admit?: "week" | "average";
  /** "auto": the gaps below. "anyway": a MAV gap from every row with a band
   *  and candidates, furthest first, for «Build one anyway». */
  mode?: "auto" | "anyway";
};

type Part = Pick<TodayGap, "gap" | "targetSets" | "candidates"> & { target?: "mav" };

/** One gap of the draft, from a row and what makes it a gap. */
const toGap = (r: CoverageRow, p: Part): TodayGap => ({
  muscle: r.landmark.muscle,
  gap: p.gap,
  ...(p.target ? { target: p.target } : {}),
  targetSets: p.targetSets,
  weekSets: r.landmark.weeklySets,
  lastTrainedMs: r.lastTrainedMs ?? null,
  candidates: p.candidates,
});

const fromWeek = (w: RowWeekFill): Part => ({
  gap: w.gap,
  targetSets: w.targetSets,
  candidates: w.candidates,
});

/** The MEV half of a row: its 4-week proposal, or (admit "week") its weekFill. */
const mevPart = (r: CoverageRow, week: boolean): Part | null => {
  if (r.proposal?.kind === "proposal") {
    const candidates = r.proposal.draftCandidates ?? r.proposal.candidates;
    return candidates.length > 0
      ? { gap: r.proposal.gap, targetSets: r.proposal.mev, candidates }
      : null;
  }
  return week && r.weekFill?.target === "mev" ? fromWeek(r.weekFill) : null;
};

/** The MAV half: the 4-week fill, or (admit "week") a weekFill on MAV. */
const mavPart = (r: CoverageRow, week: boolean): Part | null => {
  const part: Part | null = r.mavFill
    ? { gap: r.mavFill.gap, targetSets: r.mavFill.mav, candidates: r.mavFill.candidates }
    : week && r.weekFill?.target === "mav"
      ? fromWeek(r.weekFill)
      : null;
  return part && { ...part, target: "mav" };
};

const byGapDesc = (a: TodayGap, b: TodayGap) => b.gap - a.gap;

/** «Build one anyway»: a MAV gap from every banded row with candidates. */
const anywayGaps = (rows: ReadonlyArray<CoverageRow>): TodayGap[] =>
  rows
    .flatMap((r): TodayGap[] => {
      const band = r.landmark.band;
      const candidates =
        r.proposal?.kind === "proposal"
          ? (r.proposal.draftCandidates ?? r.proposal.candidates)
          : (r.mavFill?.candidates ?? r.weekFill?.candidates ?? []);
      const gap = band ? band.mav[0] - r.landmark.weeklySets : 0;
      return band && gap > 0 && candidates.length > 0
        ? [toGap(r, { gap, targetSets: band.mav[0], candidates, target: "mav" })]
        : [];
    })
    .sort(byGapDesc);

/** AP-9b: the gaps «Build today's session» starts from: the MEV gaps in the
 *  rows' order, then the MAV fills, furthest below MAV first (AP-9c). P0
 *  57d74690 (D3): each gap carries its band value and this week's hard sets,
 *  so the draft measures today's gap on the week; `gap` stays the 4-week one,
 *  the Progress list's. D-b: admit "week" also takes the rows `weekFill`
 *  found. */
export const todayGapsFromCoverage = (
  coverage: MuscleCoverage | null,
  opts: TodayGapsOpts = {},
): TodayGap[] => {
  const rows = coverage?.rows ?? [];
  if (opts.mode === "anyway") return anywayGaps(rows);
  const week = (opts.admit ?? "week") === "week";
  const part = (r: CoverageRow, pick: typeof mevPart): TodayGap[] => {
    const p = pick(r, week);
    return p ? [toGap(r, p)] : [];
  };
  return [
    ...rows.flatMap((r) => part(r, mevPart)),
    ...rows.flatMap((r) => part(r, mavPart)).sort(byGapDesc),
  ];
};
